import { Component, Listen, Prop, State, h, Event, EventEmitter, Watch, Element } from '@stencil/core';
import { getAddressName, searchByGeo, searchText } from './search';
import { GeoLocationJson, setClearSiteLocationCookie, FacetChangeEvent, FacetUpdateEvent, logDebug, logError, FacetItem } from '@hpfc/fc-frontend-core';

interface Place {
  id: string;
  place_name: string;
  geometry: {
    coordinates: string[];
  };
}

@Component({
  tag: 'fccd-location-search',
  styleUrl: 'location-search.scss',
})
export class locationSearch {
  @Element() element: HTMLElement;

  /**
   * name for the enclosed form field
   */
  @Prop() name: string;

  /**
   * Mapbox API key
   */
  @Prop() accessToken: string;
  /**
   * Country of origin default to US
   */
  @Prop() country: string = 'US';
  /**
   * comma list of Data Type see https://docs.mapbox.com/api/search/geocoding/#data-types for available types.
   */
  @Prop() types: string = 'postcode,place';
  /**
   * Bias the response to favor results that are closer to this location, in longitude,latitude
   * see https://docs.mapbox.com/api/search/geocoding/#forward-geocoding for details.
   */
  @Prop() proximity: string = '-93.2650,44.9778';
  /**
   * The name of the location.
   */
  @Prop() where: string;
  /**
   * The Element id.
   */
  @Prop() elmid: string;
  /**
   * Is error state
   */
  @Prop() error: boolean = false;
  /**
   * error message, if is error
   */
  @Prop() errorMessage: string = '';
  /**
   * Hides the search button
   */
  @Prop() hideSearch: boolean = false;

  @State() results: Place[] = [];
  @State() autocompleteOpen: any = false;
  @State() geoLocationJson: GeoLocationJson;
  /**
   * The index of the currently selected option in the dropdown list.
   */
  @State() focus: number;
  /**
   * The id of the currently active element in the dropdown list, so the screen reader knows what to announce.
   */
  @State() activeDescendant: string;
  /**
   * 'true' if the field is required, 'false' otherwise.
   */
  @Prop() required?: boolean = false;
  /**
   * 'true' we should hide the optional label, 'false' otherwise.
   */
  @Prop() hideInputOptionalLabel: boolean = true;
  /**
   * Dispatches this event when a selection is made.
   */
  @Event() fccdFacetChangeEvent: EventEmitter<FacetChangeEvent>;

  /**
   * Any time the focus index changes, the activeDescendant should also change.
   */
  @Watch('focus')
  watchHandler(newValue: number) {
    if (!this.focus && this.focus !== 0) {
      this.activeDescendant = undefined;
      return;
    }
    this.activeDescendant = `${this.elmid}-autocomplete-${newValue}`;
  }

  @Listen('fccdComponentEvent', { target: 'document' })
  updateHandler(event: CustomEvent<FacetUpdateEvent>) {
    if (event?.detail?.facetMap) {
      if (event.detail.facetMap.has(this.elmid) && event.detail.facetMap.get(this.elmid).value instanceof GeoLocationJson) {
        this.geoLocationJson = event.detail.facetMap.get(this.elmid).value as GeoLocationJson;
        logDebug('caught fccdComponentEvent for element: ' + this.elmid);
      }
    }
  }

  @Listen('hp5LocationChange')
  locationChange(event) {
    if (event.target.getAttribute('element-id') === this.elmid) {
      this.handleLocationChange(event);
    }
  }

  @Listen('hp5InputSearch')
  inputSearch(event) {
    if (event.target.getAttribute('element-id') === this.elmid) {
      if (this.focus == null) {
        this.clickOption(this.results[0], 0);
      }
    }
  }

  @Listen('click', { target: 'body' })
  inputClick(event) {
    if (!this.element.contains(event.target)) {
      const input = document.querySelector(`#${this.elmid}`);
      const inputValue = input['value'];

      // if the input is currently empty and we are outside of the component, re-set to Minneapolis, MN
      if (!inputValue) {
        setClearSiteLocationCookie();
        this.handleChange(undefined, new GeoLocationJson(undefined, undefined, undefined));
      }
      this.close(event);
    }
  }

  componentWillLoad() {
    if (!this.geoLocationJson && this.where) {
      this.geoLocationJson = new GeoLocationJson(this.where, '', '');
    }
  }

  componentDidLoad() {
    const input = document.querySelector(`#${this.elmid}`);
    if (input) {
      input.addEventListener('keydown', event => {
        this.onKeyDown(event);
      });
      input.addEventListener('focus', event => {
        this.displayAutocompleteOnFocus(event);
      });
    }
  }

  close = async event => {
    const text = event.target.value;
    if (this.results && !text && this.autocompleteOpen) {
      this.results = [];
      this.autocompleteOpen = false;
    }
  };

  displayAutocompleteOnFocus = async event => {
    const text = event.target.value;
    if (text && text != this.where) {
      const searchCriteria = { country: this.country, types: this.types };
      const results = await searchText(text, this.accessToken, searchCriteria);
      this.results = results.features;
      this.focus = null;
      this.autocompleteOpen = true;
    }
  };

  onKeyDown = e => {
    switch (e.which) {
      // up
      case 38:
        e.preventDefault();
        this.moveFocus(-1);
        break;
      // down
      case 40:
        e.preventDefault();
        this.moveFocus(1);
        break;
      // accept enter (13) or accept tab (9)
      case 13:
      case 9:
        this.handleEnterOrTab(e);
        break;
    }
  };

  handleEnterOrTab = e => {
    if (this.results.length > 0 && this.focus == null) {
      this.clickOption(this.results[0], 0);
    } // if the input is empty + we tab away, we want to reset to Minneapolis, MN
    else if ((e.target && !e.target.value && this.where) || (e.which == 9 && !e.target.value)) {
      setClearSiteLocationCookie();
      this.handleChange(undefined, new GeoLocationJson(undefined, undefined, undefined));
    }
    this.acceptFocus(e);
  };

  acceptFocus = async (event: CustomEvent) => {
    if (this.focus !== null) {
      const place = this.results[this.focus];
      if (place) {
        this.geoLocationJson = new GeoLocationJson(place.place_name, place.geometry.coordinates[1], place.geometry.coordinates[0]);

        this.handleChange(event, this.geoLocationJson);
        this.results = [];
        this.focus = null;
        this.autocompleteOpen = false;
      }
    }
    return;
  };

  moveFocus = (dir: number) => {
    if (this.focus === null || this.focus === undefined) {
      this.focus = 0;
      return;
    }
    const resultSize = this.results.length - 1;
    const newIndex = this.focus + dir;
    // loops focus to top/bottom if the end of the list is reached
    if (newIndex < 0) {
      this.focus = resultSize;
    } else if (newIndex > resultSize) {
      this.focus = 0;
    } else {
      this.focus = newIndex;
    }
  };

  handleLocationChange = async event => {
    const { detail } = event;
    const { lat, lng } = detail;

    if (lat && lng) {
      const searchCriteria = { country: this.country, types: this.types, limit: 1 };
      const result = await searchByGeo(lat, lng, this.accessToken, searchCriteria);
      const address = getAddressName(result);
      this.handleChange(event, new GeoLocationJson(address, lat, lng));
    } else {
      logError('lat/lng information is missing');
    }
  };

  onInput = async (e: InputEvent) => {
    const value: string = e.target['value'];
    if (value != '') {
      const searchCriteria = { country: this.country, types: this.types, proximity: this.proximity };
      const results = await searchText(value, this.accessToken, searchCriteria);
      this.results = results.features;
      this.autocompleteOpen = true;
      this.focus = null;
    } else {
      this.results = [];
      this.focus = null;
      this.autocompleteOpen = false;
    }
  };

  clickOption = async (place: Place, listLocation: number) => {
    const geoLocationJson: GeoLocationJson = new GeoLocationJson(place.place_name, place.geometry.coordinates[1], place.geometry.coordinates[0]);

    this.handleChange(null, geoLocationJson);
    this.results = [];
    this.focus = listLocation;
    this.autocompleteOpen = false;
  };

  handleChange(event: CustomEvent, geoLocationJson: GeoLocationJson) {
    this.geoLocationJson = geoLocationJson;
    const facetItem = new FacetItem(this.elmid, geoLocationJson, true);
    this.fccdFacetChangeEvent.emit(new FacetChangeEvent(event, facetItem));
  }

  render() {
    return (
      <div class="mapbox-geolocation">
        <hp5-input-search-location
          name={this.name}
          element-id={this.elmid}
          geolocationEnabled={true}
          geolocationLabel="Use current location"
          label="City, state or ZIP"
          labelAlignment="left"
          inputDisabled={false}
          error={this.error}
          error-message={this.errorMessage}
          required={this.required}
          hideInputOptionalLabel={false}
          location={JSON.stringify(this.geoLocationJson)}
          hide-search-button={this.hideSearch}
          hide-input-optional-label={this.hideInputOptionalLabel}
        >
          <input
            aria-activedescendant={this.activeDescendant}
            aria-autocomplete="list"
            aria-controls={`${this.elmid}-autocomplete-results`}
            aria-expanded={this.results.length > 0 ? 'true' : 'false'}
            aria-haspopup="listbox"
            onInput={this.onInput}
            role="combobox"
          />
        </hp5-input-search-location>
        <hp5-semantic size-selection="sm" class="autocomplete-container" no-bullets="true">
          <ul id={`${this.elmid}-autocomplete-results`} role="listbox">
            {this.results.length > 0 &&
              this.results.map((result, i) => {
                const isActive = i === this.focus;
                const elementId = `${this.elmid}-autocomplete-${i}`;
                return (
                  <li
                    aria-selected={isActive}
                    class={isActive ? 'autocomplete-focus' : ''}
                    id={elementId}
                    key={result.id}
                    onClick={() => this.clickOption(result, i)}
                    role="option"
                  >
                    {result.place_name}
                  </li>
                );
              })}
          </ul>
        </hp5-semantic>
      </div>
    );
  }
}
